export type KeyName =
  | "ArrowUp"
  | "ArrowDown"
  | "ArrowLeft"
  | "ArrowRight"
  | "Enter"
  | "Escape"
  | "Shift"
  | "Tab";

const MAP: Record<string, KeyName> = {
  ArrowUp: "ArrowUp",
  ArrowDown: "ArrowDown",
  ArrowLeft: "ArrowLeft",
  ArrowRight: "ArrowRight",
  w: "ArrowUp",
  W: "ArrowUp",
  s: "ArrowDown",
  S: "ArrowDown",
  a: "ArrowLeft",
  A: "ArrowLeft",
  d: "ArrowRight",
  D: "ArrowRight",
  Enter: "Enter",
  z: "Enter",
  Z: "Enter",
  " ": "Enter",
  Escape: "Escape",
  x: "Escape",
  X: "Escape",
  Backspace: "Escape",
  Shift: "Shift",
  Tab: "Tab",
};

/** Keys a DOM text field owns while it has focus. */
function typing(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
}

export class Input {
  down = new Set<KeyName>();
  pressed = new Set<KeyName>();
  /** Single printable keys pressed this frame (lower-case), for shortcuts like chat and emotes. */
  raw = new Set<string>();
  private hold = new Map<KeyName, number>();

  attach(root: HTMLElement): void {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    root.querySelectorAll<HTMLButtonElement>("[data-key]").forEach((btn) => {
      const key = btn.dataset.key as KeyName | undefined;
      if (!key) return;
      const start = (e: Event) => {
        e.preventDefault();
        this.feed(key, true);
      };
      const end = (e: Event) => {
        e.preventDefault();
        this.feed(key, false);
      };
      btn.addEventListener("pointerdown", start);
      btn.addEventListener("pointerup", end);
      btn.addEventListener("pointerleave", end);
      btn.addEventListener("pointercancel", end);
    });
    root.querySelectorAll<HTMLButtonElement>("[data-raw]").forEach((btn) => {
      btn.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        if (btn.dataset.raw) this.raw.add(btn.dataset.raw);
      });
    });
    window.addEventListener("blur", () => this.reset());
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.reset();
    });
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (typing(e)) return;
    if (e.key.length === 1 && !e.repeat) this.raw.add(e.key.toLowerCase());
    const key = MAP[e.key];
    if (!key) return;
    e.preventDefault();
    if (e.repeat) return;
    this.feed(key, true);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (typing(e)) return;
    const key = MAP[e.key];
    if (!key) return;
    e.preventDefault();
    this.feed(key, false);
  };

  feed(key: KeyName, isDown: boolean): void {
    if (isDown) {
      if (!this.down.has(key)) this.pressed.add(key);
      this.down.add(key);
    } else {
      this.down.delete(key);
      this.hold.delete(key);
    }
  }

  consume(key: KeyName): boolean {
    if (this.pressed.has(key)) {
      this.pressed.delete(key);
      return true;
    }
    return false;
  }

  consumeAny(...keys: KeyName[]): boolean {
    let consumed = false;
    for (const key of keys) if (this.consume(key)) consumed = true;
    return consumed;
  }

  dirPressed(): { x: number; y: number } | null {
    if (this.consume("ArrowUp")) return { x: 0, y: -1 };
    if (this.consume("ArrowDown")) return { x: 0, y: 1 };
    if (this.consume("ArrowLeft")) return { x: -1, y: 0 };
    if (this.consume("ArrowRight")) return { x: 1, y: 0 };
    return null;
  }

  heldDir(now: number, initial = 110, repeat = 55, eager = true): { x: number; y: number } | null {
    const order: [KeyName, number, number][] = [
      ["ArrowUp", 0, -1],
      ["ArrowDown", 0, 1],
      ["ArrowLeft", -1, 0],
      ["ArrowRight", 1, 0],
    ];
    for (const [key, x, y] of order) {
      if (!this.down.has(key)) {
        this.hold.delete(key);
        continue;
      }
      const t = this.hold.get(key);
      if (t === undefined) {
        this.hold.set(key, now);
        if (eager) return { x, y };
        continue;
      }
      if (now - t >= initial) {
        this.hold.set(key, now - initial + repeat);
        return { x, y };
      }
    }
    return null;
  }

  /** Tap plus hold-repeat for menus. First held frame waits, so a short press never double-steps. */
  menuDir(now: number): { x: number; y: number } | null {
    const tap = this.dirPressed();
    // Arm on the tap frame; a slow next frame must not restart the initial delay.
    const held = this.heldDir(now, 110, 55, false);
    return tap ?? held;
  }

  private pad = new Set<KeyName>();

  /** Feed the first connected gamepad (standard mapping) through the same key states. */
  poll(_now: number): void {
    const pads = typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : [];
    const gp = Array.from(pads ?? []).find((p) => p && p.connected);
    const next = new Set<KeyName>();
    if (gp) {
      const b = (i: number) => !!gp.buttons[i]?.pressed;
      const ax = gp.axes[0] ?? 0, ay = gp.axes[1] ?? 0;
      if (b(12) || ay < -0.55) next.add("ArrowUp");
      if (b(13) || ay > 0.55) next.add("ArrowDown");
      if (b(14) || ax < -0.55) next.add("ArrowLeft");
      if (b(15) || ax > 0.55) next.add("ArrowRight");
      if (b(0)) next.add("Enter");
      if (b(1)) next.add("Escape");
      if (b(2) || b(5)) next.add("Shift");
      if (b(9) || b(3)) next.add("Tab");
    }
    for (const key of this.pad) if (!next.has(key)) this.feed(key, false);
    for (const key of next) if (!this.pad.has(key)) this.feed(key, true);
    this.pad = next;
  }

  consumeRaw(ch: string): boolean {
    return this.raw.delete(ch);
  }

  endFrame(): void {
    this.pressed.clear();
    this.raw.clear();
  }

  reset(): void {
    this.down.clear();
    this.pressed.clear();
    this.raw.clear();
    this.hold.clear();
  }
}
